/* eslint-disable react/require-default-props -- optional props use destructuring defaults; defaultProps removed for React 19 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FlexColumn } from '../GridsAndLayouts/GridsAndLayouts';
import Multiselect from './Multiselect';
import MultiselectGroup from './MultiselectGroup';
import DropdownCloseContext from './dropdownContext';
import './Dropdown.scss';

export { DropdownCloseContext };

const Dropdown = ({
  trigger,
  children,
  align = 'left',
  className = '',
  onOpen,
  onClose,
  disabled = false,
  triggerAriaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    if (onClose) onClose();
  }, [onClose]);

  const toggleDropdown = () => {
    if (disabled) return;
    const newState = !isOpen;
    setIsOpen(newState);
    if (newState && onOpen) onOpen();
    if (!newState && onClose) onClose();
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleDropdown();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeDropdown]);

  useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false);
      if (onClose) onClose();
    }
  }, [disabled, isOpen, onClose]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, closeDropdown]);

  return (
    <DropdownCloseContext.Provider value={closeDropdown}>
      <FlexColumn
        ref={dropdownRef}
        inline
        fullWidth={false}
        align="stretch"
        className={`ds-dropdown ${className}`.trim()}
      >
        <FlexColumn
          className="ds-dropdown__trigger"
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-expanded={isOpen}
          aria-haspopup="true"
          aria-label={triggerAriaLabel}
          aria-disabled={disabled || undefined}
          align="stretch"
          fullWidth
        >
          {trigger}
        </FlexColumn>

        {isOpen && (
          <FlexColumn className={`ds-dropdown__menu ds-dropdown__menu--${align}`} role="menu" align="stretch" fullWidth>
            {children}
          </FlexColumn>
        )}
      </FlexColumn>
    </DropdownCloseContext.Provider>
  );
};

Dropdown.propTypes = {
  trigger: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  align: PropTypes.oneOf(['left', 'right', 'center']),
  className: PropTypes.string,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  disabled: PropTypes.bool,
  triggerAriaLabel: PropTypes.string,
};

const DropdownMultiselect = (props) => <Multiselect DropdownComponent={Dropdown} {...props} />;
DropdownMultiselect.displayName = 'DropdownMultiselect';
Dropdown.Multiselect = DropdownMultiselect;

const DropdownMultiselectGroup = (props) => <MultiselectGroup DropdownComponent={Dropdown} {...props} />;
DropdownMultiselectGroup.displayName = 'DropdownMultiselectGroup';
Dropdown.MultiselectGroup = DropdownMultiselectGroup;

export default Dropdown;
